import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabaseReal, setDemoMode } from '../lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DemoModeContextValue {
  isDemoMode: boolean;
  toggleDemoMode: () => Promise<void>;
  loading: boolean;
}

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useDemoMode = (): DemoModeContextValue => {
  const ctx = useContext(DemoModeContext);
  if (!ctx) {
    return { isDemoMode: false, toggleDemoMode: async () => {}, loading: false };
  }
  return ctx;
};

// ─── Provider ─────────────────────────────────────────────────────────────────

interface DemoModeProviderProps {
  userRole?: string;
  children: React.ReactNode;
}

export const DemoModeProvider: React.FC<DemoModeProviderProps> = ({ userRole, children }) => {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const isSuperAdmin = userRole?.toLowerCase() === 'super admin';

  // Initial: Status aus localStorage + DB lesen
  useEffect(() => {
    if (!isSuperAdmin) {
      setActive(false);
      setDemoMode(false);
      return;
    }

    // Schneller Cache aus localStorage
    const cached = localStorage.getItem('immo_demo_mode');
    if (cached === 'true') {
      setActive(true);
      setDemoMode(true);
    }

    // DB-Status lesen (immer echten Client verwenden!)
    supabaseReal
      .from('portal_modules')
      .select('is_active')
      .eq('id', 'demo_modus')
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setActive(data.is_active);
          setDemoMode(data.is_active);
          localStorage.setItem('immo_demo_mode', String(data.is_active));
        }
      });
  }, [isSuperAdmin]);

  const toggleDemoMode = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);

    const newState = !active;

    try {
      // DB aktualisieren (echten Client verwenden!)
      await supabaseReal
        .from('portal_modules')
        .upsert({ id: 'demo_modus', name: 'Demo-Modus', is_active: newState }, { onConflict: 'id' });

      // State setzen
      setActive(newState);
      setDemoMode(newState);
      localStorage.setItem('immo_demo_mode', String(newState));

      // Seite neu laden, damit alle Komponenten die Demo-Daten bekommen
      window.location.reload();
    } catch (err) {
      console.error('Demo-Modus Toggle fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, [active, isSuperAdmin]);

  const value = useMemo<DemoModeContextValue>(() => ({
    isDemoMode: active,
    toggleDemoMode,
    loading,
  }), [active, toggleDemoMode, loading]);

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
};

export default DemoModeContext;
